import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainHeading from "../../components/main-heading.component";
import config from "../../config";
import { _get } from "../../services/api.service";
import "../content-engine-v2/content-engine.css";
import "../content-engine-v2/script-detail/ScriptDetail.css";
import "../video-create/CreateVideo.css";
import "./VideoProjects.css";
import {
  ALL_PIPELINE_STEPS,
  formatDate,
  formatDuration,
  getDisplayScenes,
  getPipelineProgress,
  getPipelineStepStatus,
  getStatusMeta,
  STAGE_LABELS,
  TERMINAL_STATUSES,
} from "./projectUtils";

const POLL_TIMEOUT = 25;
const POLL_RETRY_DELAY_MS = 3000;
const VIDEO_PREVIEW_TIME_SEC = 1;

interface VideoProjectDetail {
  id: number;
  status: string;
  mode?: string;
  topic?: string;
  stages_completed: string[];
  available_stages: string[];
  video_url: string;
  thumbnail_url?: string;
  duration: number | null;
  error_type: string;
  error_message: string;
  content_format_id: number | null;
  content_format: { id: number; name: string; slug: string } | null;
  content: { narration?: string; blocks?: unknown[] } | null;
  video_spec: {
    template: string;
    scenes: { id: number; type: string; title: string; startTime: number; endTime: number }[];
    metadata: Record<string, unknown>;
  } | null;
  created_at: string;
  updated_at: string;
}

function StatusBadge({ status }: { status: string }) {
  const meta = getStatusMeta(status);
  const isRendering = status === "rendering";
  return (
    <span
      className={`vp-status-badge${isRendering ? " rendering" : ""}`}
      style={{ color: meta.color, background: meta.bg }}
    >
      <span className="vp-status-dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

const VideoProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<VideoProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const statusRef = useRef("");

  const isTerminal = (status: string) => TERMINAL_STATUSES.includes(status);

  const loadProject = useCallback(async (options?: { manual?: boolean }) => {
    try {
      const data = await _get(`video-engine/projects/${id}/`, {}, true);
      if (data) {
        statusRef.current = data.status;
        setProject(data);
        setLastPolledAt(new Date());
      }
    } catch (err) {
      console.error("Failed to load video project", err);
    } finally {
      if (!options?.manual) {
        setLoading(false);
      }
      setRefreshing(false);
    }
  }, [id]);

  const handleRefresh = () => {
    setRefreshing(true);
    void loadProject({ manual: true });
  };

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let first = true;

    const longPoll = async () => {
      if (cancelled) return;
      const since = statusRef.current;
      const data = await _get(
        `video-engine/projects/${id}/poll/?timeout=${POLL_TIMEOUT}&since=${since}`,
        {},
        true,
      );
      if (cancelled) return;
      if (first) {
        first = false;
        setLoading(false);
      }
      if (data) {
        statusRef.current = data.status;
        setProject(data);
        setLastPolledAt(new Date());
      }
      if (!data || !isTerminal(data.status)) {
        timer = setTimeout(longPoll, data ? 0 : POLL_RETRY_DELAY_MS);
      }
    };

    longPoll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setHasStarted(false);
    const video = videoRef.current;
    if (project?.status !== "completed" || !video) return;

    const showFirstFrame = () => {
      try {
        if (
          video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
          video.currentTime < VIDEO_PREVIEW_TIME_SEC
        ) {
          video.currentTime = VIDEO_PREVIEW_TIME_SEC;
        }
      } catch {
        // Ignore seek errors before metadata is ready.
      }
    };

    video.load();
    video.addEventListener("loadeddata", showFirstFrame);
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      showFirstFrame();
    }

    return () => {
      video.removeEventListener("loadeddata", showFirstFrame);
    };
  }, [project?.status, project?.video_url]);

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    setHasStarted(true);
    video.currentTime = 0;
    void video.play();
  };

  const primeFirstFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || hasStarted) return;

    try {
      if (
        video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        video.currentTime < VIDEO_PREVIEW_TIME_SEC
      ) {
        video.currentTime = VIDEO_PREVIEW_TIME_SEC;
      }
    } catch {
      // Ignore seek errors before the browser has enough metadata.
    }
  }, [hasStarted]);

  const videoSrc = project?.video_url
    ? project.video_url.includes("#")
      ? project.video_url
      : `${project.video_url}#t=${VIDEO_PREVIEW_TIME_SEC}`
    : "";

  const completed = project?.status === "completed";
  const failed = project?.status === "failed";
  const inProgress = project && !isTerminal(project.status);
  const pipelineProgress = project
    ? getPipelineProgress(project)
    : {
        progressPct: 0,
        currentStep: 1,
        totalSteps: ALL_PIPELINE_STEPS.length,
        currentLabel: "Starting",
        activeStageKey: "",
        isIndeterminate: true,
      };
  const { scenes: displayScenes, isEstimate: scenesAreEstimated } = project
    ? getDisplayScenes(project)
    : { scenes: [], isEstimate: false };

  const handleDownload = async () => {
    if (!project?.id || !project.video_url) return;
    try {
      const token = localStorage.getItem("TOKEN");
      const response = await axios.get(
        `${config.apiUrl}video-engine/projects/${project.id}/download/`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      const blob = new Blob([response.data], { type: "video/mp4" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `video-project-${project.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid video-project-detail-page">
        <MainHeading title="Video Project" />
        <div className="vp-skeleton-grid" style={{ maxWidth: 900 }}>
          <div className="vp-skeleton-card" style={{ height: 420 }} />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container-fluid video-project-detail-page">
        <MainHeading title="Video Project" />
        <div className="vp-empty">
          <p className="vp-empty-text">Project not found.</p>
          <button
            type="button"
            className="cv-btn cv-btn-secondary"
            onClick={() => navigate("/video-projects")}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  const pageTitle = project.topic?.trim() || `Video Project #${project.id}`;
  const displayTitle =
    pageTitle.charAt(0).toUpperCase() + pageTitle.slice(1);
  const subtitleParts = [`Created ${formatDate(project.created_at)}`];
  if (project.content_format?.name) {
    subtitleParts.push(project.content_format.name);
  }

  return (
    <div className="container-fluid video-project-detail-page">
      <MainHeading
        title={
          <span className="vp-detail-title-row">
            {displayTitle}
            <StatusBadge status={project.status} />
          </span>
        }
        subTitle={subtitleParts.join(" · ")}
      />

      <div className="vp-detail-topbar">
        <button
          type="button"
          className="sd-btn-link-back"
          onClick={() => navigate("/video-projects")}
        >
          ← Back to Projects
        </button>
        <div className="vp-detail-topbar-actions">
          {lastPolledAt && (
            <span className="vp-last-sync">
              {inProgress && <span className="vp-live-dot" aria-hidden />}
              Updated{" "}
              {lastPolledAt.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          )}
          <button
            type="button"
            className={`vp-refresh-btn${refreshing ? " is-refreshing" : ""}`}
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh project"
          >
            <RefreshIcon className="vp-refresh-icon" sx={{ fontSize: 18 }} />
            <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
          </button>
        </div>
      </div>

      <div className="vp-layout">
        <div className="vp-detail-grid">
          <div className="vp-player-shell">
            {completed && project.video_url ? (
              <>
                <div className="vp-player-frame">
                  <div className="vp-player-inner">
                    <video
                      ref={videoRef}
                      src={videoSrc}
                      controls={hasStarted}
                      preload="auto"
                      playsInline
                      onLoadedMetadata={primeFirstFrame}
                      onLoadedData={primeFirstFrame}
                      onPlay={() => setHasStarted(true)}
                    />
                    {!hasStarted && (
                      <div className="vp-play-overlay" onClick={handlePlay} role="presentation">
                        <PlayCircleOutlineIcon
                          sx={{
                            fontSize: 48,
                            color: "white",
                            filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.5))",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="cv-btn cv-btn-primary vp-download-btn"
                  onClick={handleDownload}
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                  Download video
                </button>
              </>
            ) : inProgress ? (
              <div className="vp-processing">
                <h2 className="vp-processing-title">
                  {project.status === "rendering" ? "Rendering your video" : "Building your video"}
                </h2>
                <p className="vp-processing-text">
                  {project.status === "rendering"
                    ? "Your video is being generated. This usually takes a minute or two — we'll update automatically."
                    : "Running the production pipeline. This page refreshes automatically."}
                </p>
                <div className="vp-progress-card">
                  <div className="vp-progress-ring" style={{ "--vp-progress": pipelineProgress.progressPct } as CSSProperties}>
                    <span className="vp-progress-value">{pipelineProgress.progressPct}%</span>
                  </div>
                  <div className="vp-progress-bar">
                    <div
                      className={`vp-progress-fill${pipelineProgress.isIndeterminate ? " is-indeterminate" : ""}${project.status === "rendering" ? " rendering" : ""}`}
                      style={{
                        width: pipelineProgress.isIndeterminate
                          ? undefined
                          : `${Math.max(pipelineProgress.progressPct, 4)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : failed ? (
              <div className="vp-failed">
                <ErrorOutlineIcon sx={{ fontSize: 48, color: "#ef4444", mb: 2 }} />
                <h2 className="vp-processing-title">Render failed</h2>
                <p className="vp-processing-text">
                  {project.error_message || "Something went wrong while generating your video."}
                </p>
                <button type="button" className="cv-btn cv-btn-primary" onClick={() => loadProject()}>
                  Try again
                </button>
              </div>
            ) : (
              <div className="vp-processing">
                <PlayCircleOutlineIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 2 }} />
                <p className="vp-processing-text">No video available yet for this project.</p>
              </div>
            )}
          </div>

          <div>
            {!completed && (
              <div className="vp-panel">
                <h2 className="vp-panel-title">Pipeline progress</h2>
                {ALL_PIPELINE_STEPS.map((stage) => {
                  const stepStatus = getPipelineStepStatus(stage, project);
                  return (
                    <div key={stage} className="vp-pipeline-step">
                      <div className={`vp-pipeline-icon ${stepStatus}`}>
                        {stepStatus === "done" ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        ) : stepStatus === "active" ? (
                          <RefreshIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <PendingOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                      </div>
                      <span
                        className={`vp-pipeline-label${stepStatus === "pending" ? " muted" : ""}`}
                      >
                        {STAGE_LABELS[stage] ?? stage}
                      </span>
                      {stepStatus === "active" && (
                        <span className="vp-pipeline-badge">Running</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {displayScenes.length > 0 && (
              <div className="vp-panel">
                <div className="vp-panel-title-row">
                  <h2 className="vp-panel-title">Scene breakdown</h2>
                  {scenesAreEstimated && (
                    <span className="vp-scene-estimate-badge">Estimated timing</span>
                  )}
                </div>
                {displayScenes.map((scene) => (
                  <div key={scene.id} className="vp-scene-row">
                    <span className="vp-scene-type">{scene.type.replace(/_/g, " ")}</span>
                    <span className="vp-scene-title">{scene.title}</span>
                    <span className="vp-scene-time">
                      {formatDuration(scene.startTime)} – {formatDuration(scene.endTime)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="vp-panel">
              <h2 className="vp-panel-title">Project details</h2>
              <div className="vp-detail-row">
                <span className="vp-detail-label">Format</span>
                <span className="vp-detail-value">
                  {project.content_format?.name || project.video_spec?.template || "—"}
                </span>
              </div>
              <div className="vp-detail-row">
                <span className="vp-detail-label">Scenes</span>
                <span className="vp-detail-value">{String(displayScenes.length)}</span>
              </div>
              <div className="vp-detail-row">
                <span className="vp-detail-label">Duration</span>
                <span className="vp-detail-value">
                  {formatDuration(
                    project.duration ??
                      project.video_spec?.scenes?.at(-1)?.endTime ??
                      displayScenes.at(-1)?.endTime ??
                      null,
                  )}
                </span>
              </div>
              <div className="vp-detail-row">
                <span className="vp-detail-label">Last updated</span>
                <span className="vp-detail-value">{formatDate(project.updated_at)}</span>
              </div>
              {failed && project.error_type && (
                <div className="vp-detail-row">
                  <span className="vp-detail-label">Error type</span>
                  <span className="vp-detail-value">{project.error_type}</span>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoProjectDetail;
